import { Button } from '../../buttons/Button';
import { Edit, Trash2 } from 'lucide-react';
import { DataTable } from '../../data-display/DataTable';
import type { ColumnDef, Row } from '@tanstack/react-table';
import React, { createElement } from 'react';
import type { DataTableWithActionsProps } from '../../../interfaces/DataTableWithActionsProps';
import type { FieldValues } from 'react-hook-form';
import type { RelatedDataConfig } from '../../../types/RelatedDataConfig';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../overlay/tooltip';

export function DataTableWithActions<TData extends FieldValues>({
  data,
  isLoading,
  mappings,
  onEdit,
  onDelete,
  isDeleting,
  customActions,
  relatedData = {},
  updateItemField
}: DataTableWithActionsProps<TData>) {
  const columns: ColumnDef<TData>[] = [
    ...Object.entries(mappings)
      .filter(([_, mapping]) => !mapping.hideFromTable)
      .map(([key, mapping]) => ({
        accessorKey: key,
        header: mapping.label,
        cell: ({ row }: { row: Row<TData> }) => {
          const item = row.original;
          const rawValue = item[key as keyof TData];
          
          // For fields with custom components and related data dependencies
          if (mapping.component && mapping.relatedData) {
            const relatedDataForComponent = mapping.relatedData.reduce((acc: Record<string, any[]>, config: RelatedDataConfig) => {
              acc[config.key] = relatedData[config.key] || [];
              return acc;
            }, {} as Record<string, any[]>);
            
            // Get availableItems from the first relatedData config (for ExpandableRelationshipCell)
            const firstRelatedDataKey = mapping.relatedData[0]?.key || 'availableItems';
            const availableItemsData = relatedDataForComponent[firstRelatedDataKey] || [];
            
            const componentProps = {
              itemId: item.id,
              currentItems: rawValue || [],
              // For ExpandableRelationshipCell, availableItems should come from relatedData
              availableItems: availableItemsData,
              onDataChange: (newData: any) => {
                if (updateItemField) {
                  updateItemField(item.id, key, newData);
                }
              },
              // Add static props from mapping
              ...(mapping.props || {}),
              // Add related data as a single object
              relatedData: relatedDataForComponent
            };

            return createElement(mapping.component, componentProps);
          }
          
          // For other fields, use getValue if available, otherwise raw value
          return mapping.getValue ? mapping.getValue(item) : rawValue;
        }
      })),
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }: { row: Row<TData> }) => {
        const item = row.original;
        return (
          <div className="flex gap-2">
            {onEdit && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(item)}
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(item)}
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            {customActions?.map((action, index) => {
              const button = (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => action.onClick(item)}
                >
                  {action.icon}
                </Button>
              );

              if (action.tooltip) {
                return (
                  <TooltipProvider key={index}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {button}
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{action.tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }

              return <React.Fragment key={index}>{button}</React.Fragment>;
            })}
          </div>
        );
      }
    }
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
    />
  );
} 